import { z } from 'zod';

import { createFullNameSchema } from '@/app/lib/forms/schemas/base-fields.schema';
import { REGEX_TEL_HOME_MOBILE } from '@/components/shared/utility/regex';
import type { Dealer } from '@/types/tripetchgroup-cms/types';

export const AdsDataScheme = z.object({
  userId: z.string().optional(),
  gaId: z.string().optional(),
  gclId: z.string().optional(),
  fbclid: z.string().optional(),
  ttclid: z.string().optional(),
  experiment: z.string().optional(),
  variant: z.string().optional(),
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_campaign_id: z.string().optional(),
  utm_term: z.string().optional(),
  utm_content: z.string().optional(),
  utm_adgroup_id: z.string().optional(),
  utm_ad_id: z.string().optional(),
});

export const MODEL = ['ppv', 'pickup'] as const;
export type ModelType = (typeof MODEL)[number];

export const REFERRAL_INFO_SOURCE = ['self', 'friend'] as const;
export type ReferralInfoSourceType = (typeof REFERRAL_INFO_SOURCE)[number];

export const ReferralInfoSchema = z.object({
  source: z.enum(REFERRAL_INFO_SOURCE),
  name: z.string().nullable().optional(),
});
export type ReferralInfoType = z.infer<typeof ReferralInfoSchema>;

export const LmsDataSchema = z.object({
  ctsCampaignName: z
    .string()
    .min(1, { message: 'ctsCampaignName is required' }),
  ctsCampaignId: z.string().min(1, { message: 'ctsCampaignId is required' }),
  ctsCampaignVersion: z
    .string()
    .min(1, { message: 'ctsCampaignVersion is required' }),
  branchCode: z
    .string()
    // .min(1, { message: 'branchCode is required' }) // [NOTE]: for CV
    .optional(),
  scId: z.string().min(1, { message: 'scId is required' }).optional(),
  nationalId: z
    .string()
    .min(6, {
      message: 'Must has at least 6 characters for nationalId or passport',
    })
    .nullable()
    .optional(),
  firstName: createFullNameSchema(),
  mobile: z.string().regex(REGEX_TEL_HOME_MOBILE),
  model: z.enum(MODEL).nullable().optional(), // [NOTE]: for CV
  availableTime: z.string().nullable().optional(),
  lineId: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  remark: z.string().nullable().optional(),
  skipCallCenter: z.boolean().nullable().optional(),
  refTrackerId: z.string().nullable().optional(),
  lineUid: z.string().nullable().optional(),
  affiliateCode: z.string().nullable().optional(),
  referralInfo: ReferralInfoSchema.optional(),
});

export const LmsDataWithAdsSchema = LmsDataSchema.and(AdsDataScheme);

export type AdsDataType = z.infer<typeof AdsDataScheme>;
export type LmsDataWithAdsType = z.infer<typeof LmsDataWithAdsSchema>;

export interface LeadFormSuccessPayload {
  data: LmsDataWithAdsType;
  leadId?: string;
  province?: string;
  district?: string;
  dealer?: string;
}

export const DealerFormSchema = z.object({
  branchCode: z.string(),
  name_th: z.string(),
});

export type DealerFormType = z.infer<typeof DealerFormSchema> &
  Pick<
    Dealer,
    | 'lat'
    | 'lng'
    | 'googleMapUrl'
    | 'afterSaleAddress'
    | 'afterSaleOpenTimeHourMonFri'
    | 'afterSaleOpenTimeHourSat'
    | 'afterSaleOpenTimeHourSun'
    | 'afterSaleOpenTimeRemark'
    | 'afterSaleAppointmentContact'
    | 'afterSaleLineLcv'
    | 'afterSaleLineCv'
  >;

export const LmsFormDataSchema = z.object({
  provinceId: z.string().min(1),
  districtId: z.string().optional().nullable(),
  selectedDealer: DealerFormSchema.optional(),
  term: z.literal(true),
});
export type LmsFormDataType = z.infer<typeof LmsFormDataSchema>;
